import type { ReactNode } from 'react';
import { FormMessage } from '@/components/base/FormMessage';
import { Label } from '@/components/base/Label';

/**
 * 第二層版型：表單欄位網格 + 單一欄位的 label／控制項／訊息三段結構。
 *
 * 原系統對應：Bootstrap 3 的 `form-horizontal` + `.row .form-group`
 *   `<div class="row form-group">`
 *     `<label class="col-sm-3 control-label text-right">…：</label>`
 *     `<div class="col-sm-9">…控制項…</div>`
 *   `</div>`
 * 例：`layout/navbar.jsp:127-142`、`:144-158`、`:161-175`（個人登入資訊 modal 的三列）。
 * 原系統以 12 欄柵格切版，雛形改用 CSS grid 表達同樣的「n 欄」概念。
 *
 * ponytail: 用原生 CSS grid，不引入表單函式庫；驗證訊息只是 render 傳進來的字串，
 * 不做 schema 驗證（雛形階段驗證規則還沒定案）。升級路徑：欄位規則穩定後再接
 * 一層 form state library，本元件的 DOM 契約不變。
 *
 * 【證據等級】§7 第 3 級（原始碼）。【雛形值待實機校準】
 */
export type FormGridColumns = 1 | 2 | 3;

export interface FormGridProps {
  uiId: string;
  /** 一列幾欄。原系統多為單欄（col-sm-3 + col-sm-9）與雙欄混用。 */
  columns?: FormGridColumns;
  children: ReactNode;
}

// ponytail: 靜態對照表，不動態組 class（§8.1）。
const GRID_COLUMNS = {
  1: 'grid-cols-1',
  2: 'grid-cols-1 md:grid-cols-2',
  3: 'grid-cols-1 md:grid-cols-3',
} as const;

const FIELD_SPAN = {
  1: 'md:col-span-1',
  2: 'md:col-span-2',
  3: 'md:col-span-3',
} as const;

export function FormGrid({ uiId, columns = 1, children }: FormGridProps) {
  return (
    <div data-ui-id={uiId} className={`grid gap-x-6 gap-y-3 ${GRID_COLUMNS[columns]}`}>
      {children}
    </div>
  );
}

export interface FormGridFieldProps {
  uiId: string;
  label: string;
  /**
   * 對應控制項的 id。
   * 【已知限制】第一層的 TextInput/Select/Textarea 契約目前**沒有 `id` prop**
   * （見 base/TextInput.tsx），所以這個 htmlFor 實際上指不到任何節點。
   * TODO(base): 第一層加上 id prop 之後這裡才會真的生效。
   */
  htmlFor?: string;
  required?: boolean;
  /** 佔幾欄（跨欄的長欄位，例如備註）。 */
  span?: FormGridColumns;
  /** 驗證錯誤訊息；有值時同時設 aria-describedby。 */
  error?: string;
  /** 說明文字（沒有 error 時才顯示，避免兩行訊息互相干擾）。 */
  hint?: string;
  children: ReactNode;
}

export function FormGridField({
  uiId,
  label,
  htmlFor,
  required = false,
  span = 1,
  error,
  hint,
  children,
}: FormGridFieldProps) {
  const messageId = `${uiId}-message`;
  return (
    <div data-ui-id={uiId} className={`flex flex-col gap-1 ${FIELD_SPAN[span]}`}>
      <Label
        uiId={`${uiId}-label`}
        required={required}
        {...(htmlFor === undefined ? {} : { htmlFor })}
      >
        {label}
      </Label>
      {/* 第一層的 TextInput/Select 契約沒有 describedBy prop（base/TextInput.tsx 註解明說
          「欄位與訊息的 aria-describedby 關聯屬於第二層 FormGrid」，但沒有開缺口），
          所以只能把 aria-describedby 掛在控制項的外層 group 上。
          【已知限制】這對讀屏的效果不如掛在 input 本身。
          TODO(base): 請第一層加上 describedBy prop，屆時把這行移到欄位上。 */}
      <div
        data-ui-id={`${uiId}-control`}
        aria-describedby={error !== undefined || hint !== undefined ? messageId : undefined}
      >
        {children}
      </div>
      {error !== undefined ? (
        <FormMessage uiId={messageId} tone="error">
          {error}
        </FormMessage>
      ) : hint !== undefined ? (
        <FormMessage uiId={messageId} tone="hint">
          {hint}
        </FormMessage>
      ) : null}
    </div>
  );
}
